import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, doc, getDoc, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import Navigation from '../components/Navigation';
import Pagination from '../components/Pagination';

interface RacerData {
  id: string;
  racerId: string;
  fullName?: string;
  motherName?: string;
  idCardNumber?: string;
  address?: string;
  socialSecurityNumber?: string;
  email?: string;
  phoneNumber?: string;
  userId?: string;
  isProfileComplete?: boolean;
  updatedAt?: any;
}

const Racers: React.FC = () => {
  const { currentUser, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const [racers, setRacers] = useState<RacerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRacer, setSelectedRacer] = useState<RacerData | null>(null);
  const [showRacerDetails, setShowRacerDetails] = useState(false);
  const [racerStats, setRacerStats] = useState({
    total: 0,
    complete: 0,
    withUser: 0
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    if (!currentUser || !isAdmin) {
      navigate('/dashboard');
      return;
    }

    fetchRacers();
  }, [currentUser, isAdmin, navigate]);

  const fetchRacers = async () => {
    try {
      setLoading(true);

      // Vendégek lekérdezése
      const racersRef = collection(db, 'racers');
      const querySnapshot = await getDocs(racersRef);

      const racersData: RacerData[] = [];

      querySnapshot.forEach((doc) => {
        const racerData = doc.data();
        racersData.push({
          id: doc.id,
          racerId: racerData.racerId || doc.id,
          fullName: racerData.fullName || '',
          motherName: racerData.motherName || '',
          idCardNumber: racerData.idCardNumber || '',
          address: racerData.address || '',
          socialSecurityNumber: racerData.socialSecurityNumber || '',
          email: racerData.email || '',
          phoneNumber: racerData.phoneNumber || '',
          userId: racerData.userId || '',
          isProfileComplete: racerData.isProfileComplete || false,
          updatedAt: racerData.updatedAt
        });
      });

      // console.log('Lekérdezett vendégek:', racersData.length);
      setRacers(racersData);

      // Statisztikák számítása
      setRacerStats({
        total: racersData.length,
        complete: racersData.filter(racer => racer.isProfileComplete).length,
        withUser: racersData.filter(racer => racer.userId).length
      });

    } catch (error) {
      console.error('Hiba a vendégek lekérdezése során:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredRacers = racers.filter(racer =>
    (racer.fullName && racer.fullName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (racer.email && racer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (racer.racerId && racer.racerId.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (racer.phoneNumber && racer.phoneNumber.includes(searchTerm))
  );

  // Számoljuk ki a megjelenítendő vendégeket
  const paginatedRacers = filteredRacers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const viewRacerDetails = (racer: RacerData) => {
    setSelectedRacer(racer);
    setShowRacerDetails(true);
  };

  const closeRacerDetails = () => {
    setShowRacerDetails(false);
    setSelectedRacer(null);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Nincs adat';

    const date = timestamp.toDate();
    return date.toLocaleString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Mobil menü megnyitása/bezárása
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Hiba a kijelentkezés során:', error);
    }
  };

  const refreshData = () => {
    setLoading(true);
    fetchRacers();
  };

  if (!currentUser || !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation showPublicBooking={true} />

      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-900">Vendégek kezelése</h1>
            <button
              onClick={refreshData}
              className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Frissítés
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Összes vendég</dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">{racerStats.total}</dd>
                </dl>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Teljes profilok</dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">{racerStats.complete}</dd>
                </dl>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Felhasználóhoz kötve</dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">{racerStats.withUser}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2 sm:mb-0">Vendégek listája</h3>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Keresés..."
                    className="border border-gray-300 rounded-md py-2 px-4 pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-auto"
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                  <div className="absolute left-3 top-2.5">
                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-4">
                <p className="text-gray-600">Vendégek betöltése...</p>
              </div>
            ) : (
              <>
                <ul className="divide-y divide-gray-200">
                  {paginatedRacers.map((racer) => (
                    <li key={racer.id}>
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex items-center mb-2 sm:mb-0">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              {racer.fullName ? (
                                <span className="text-lg font-medium text-gray-700">
                                  {racer.fullName.charAt(0).toUpperCase()}
                                </span>
                              ) : (
                                <svg className="h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 flex flex-wrap items-center">
                                <span className="mr-1">{racer.fullName || 'Névtelen vendég'}</span>
                                <div className="flex flex-wrap mt-1 sm:mt-0">
                                  {racer.isProfileComplete && (
                                    <span className="mr-1 mb-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                      Teljes profil
                                    </span>
                                  )}
                                  {racer.userId && (
                                    <span className="mr-1 mb-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                      Felhasználóhoz kötve
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-sm text-gray-500 flex flex-col sm:flex-row sm:items-center">
                                <span className="mr-2">{racer.racerId}</span>
                                {racer.email && <span className="mr-2">{racer.email}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <button
                              onClick={() => viewRacerDetails(racer)}
                              className="px-3 py-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Részletek
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                  {filteredRacers.length === 0 && (
                    <li>
                      <div className="px-4 py-4 text-center text-gray-500">
                        Nincs a keresésnek megfelelő vendég.
                      </div>
                    </li>
                  )}
                </ul>

                {/* Pagination hozzáadása */}
                <div className="px-4 py-4 border-t border-gray-200">
                  <Pagination
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    list={filteredRacers}
                    setCurrentPage={setCurrentPage}
                    setItemsPerPage={setItemsPerPage}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Vendég részletek modal */}
      {showRacerDetails && selectedRacer && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-white pb-2 border-b">
              <h3 className="text-lg font-medium text-gray-900">Vendég részletei</h3>
              <button
                onClick={closeRacerDetails}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mt-2">
              <div className="bg-gray-50 p-4 rounded-md mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center mb-4">
                  <div className="flex-shrink-0 h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center mb-2 sm:mb-0">
                    {selectedRacer.fullName ? (
                      <span className="text-2xl font-medium text-gray-700">
                        {selectedRacer.fullName.charAt(0).toUpperCase()}
                      </span>
                    ) : (
                      <svg className="h-8 w-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                  </div>
                  <div className="sm:ml-4">
                    <h4 className="text-lg font-medium text-gray-900">
                      {selectedRacer.fullName || 'Névtelen vendég'}
                    </h4>
                    {selectedRacer.email && <p className="text-sm text-gray-500">{selectedRacer.email}</p>}
                    <div className="mt-1 flex flex-wrap">
                      {selectedRacer.isProfileComplete && (
                        <span className="mr-1 mb-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Teljes profil
                        </span>
                      )}
                      {selectedRacer.userId && (
                        <span className="mr-1 mb-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          Felhasználóhoz kötve
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Vendég azonosító:</p>
                    <p className="text-sm font-medium break-all">{selectedRacer.racerId}</p>
                  </div>
                  {selectedRacer.userId && (
                    <div>
                      <p className="text-sm text-gray-500">Felhasználó azonosító:</p>
                      <p className="text-sm font-medium break-all">{selectedRacer.userId}</p>
                    </div>
                  )}
                  {selectedRacer.fullName && (
                    <div>
                      <p className="text-sm text-gray-500">Teljes név:</p>
                      <p className="text-sm font-medium">{selectedRacer.fullName}</p>
                    </div>
                  )}
                  {selectedRacer.motherName && (
                    <div>
                      <p className="text-sm text-gray-500">Anyja neve:</p>
                      <p className="text-sm font-medium">{selectedRacer.motherName}</p>
                    </div>
                  )}
                  {selectedRacer.idCardNumber && (
                    <div>
                      <p className="text-sm text-gray-500">Személyi igazolvány szám:</p>
                      <p className="text-sm font-medium">{selectedRacer.idCardNumber}</p>
                    </div>
                  )}
                  {selectedRacer.address && (
                    <div>
                      <p className="text-sm text-gray-500">Cím:</p>
                      <p className="text-sm font-medium">{selectedRacer.address}</p>
                    </div>
                  )}
                  {selectedRacer.socialSecurityNumber && (
                    <div>
                      <p className="text-sm text-gray-500">TAJ szám:</p>
                      <p className="text-sm font-medium">{selectedRacer.socialSecurityNumber}</p>
                    </div>
                  )}
                  {selectedRacer.email && (
                    <div>
                      <p className="text-sm text-gray-500">Email:</p>
                      <p className="text-sm font-medium">{selectedRacer.email}</p>
                    </div>
                  )}
                  {selectedRacer.phoneNumber && (
                    <div>
                      <p className="text-sm text-gray-500">Telefonszám:</p>
                      <p className="text-sm font-medium">{selectedRacer.phoneNumber}</p>
                    </div>
                  )}
                  {selectedRacer.updatedAt && (
                    <div>
                      <p className="text-sm text-gray-500">Utolsó frissítés:</p>
                      <p className="text-sm font-medium">{formatDate(selectedRacer.updatedAt)}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={closeRacerDetails}
                  className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300"
                >
                  Bezárás
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Racers; 